import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type OnSelectionChangeParams,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type {
  PeopleTravelEvent,
  PeopleTravelEventKind,
  PeopleTravelLeg,
  PeopleTravelLegMode,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelStop,
  PeopleTravelTrip,
} from '../../../types';
import {
  CLUSTER_WIDTH,
  clusterSize,
  defaultEventPosition,
  nextLocationSlot,
  optionsForLeg,
  type TravelCanvasSelection,
} from '../../../utils/peopleTravelCanvasGraph';
import {
  deleteTripEvent,
  deleteTripLeg,
  deleteTripLocation,
  upsertTripEvent,
  upsertTripLeg,
  upsertTripLocation,
} from '../../../utils/peopleTravelFirestore';
import LocationClusterNode from './nodes/LocationClusterNode';
import TravelEventNode from './nodes/TravelEventNode';
import TravelLegEdge from './edges/TravelLegEdge';
import PeopleTravelPlannerToolbar from './PeopleTravelPlannerToolbar';
import PeopleTravelPlannerDock from './PeopleTravelPlannerDock';
import '../../board/InspirationBoard.css';

const nodeTypes = {
  locationCluster: LocationClusterNode,
  travelEvent: TravelEventNode,
};

const edgeTypes = {
  travelLeg: TravelLegEdge,
};

const WRITE_DEBOUNCE_MS = 400;

type Props = {
  trip: PeopleTravelTrip;
  ownerUserId: string;
  readOnly?: boolean;
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
  stops: PeopleTravelStop[];
  options: PeopleTravelOption[];
  searchBusy?: boolean;
  onSearchSelected: (selection: TravelCanvasSelection | null) => void;
};

function graphSignature(
  locations: PeopleTravelLocation[],
  events: PeopleTravelEvent[],
  legs: PeopleTravelLeg[],
  options: PeopleTravelOption[],
): string {
  return JSON.stringify({
    locations: locations.map(l => [l.id, l.label, l.x, l.y, l.sortOrder, l.startDate, l.endDate]),
    events: events.map(e => [e.id, e.locationId, e.title, e.kind, e.x, e.y, e.startAt, e.endAt, e.notes]),
    legs: legs.map(l => [l.id, l.fromLocationId, l.toLocationId, l.mode, l.selectedOptionIds, l.notes]),
    options: options.map(o => [o.id, o.legId, o.eventId, o.price, o.label, o.isSelected]),
  });
}

const PeopleTravelPlannerBoardInner: React.FC<Props> = ({
  trip,
  ownerUserId,
  readOnly,
  locations,
  events,
  legs,
  options,
  searchBusy,
  onSearchSelected,
}) => {
  const [selection, setSelection] = useState<TravelCanvasSelection | null>(null);
  const [attachSignal, setAttachSignal] = useState(0);
  const [rebuildSignal, setRebuildSignal] = useState(0);
  const isEmptyChain = locations.length === 0 && events.length === 0 && legs.length === 0;
  const [plannerOpen, setPlannerOpen] = useState(isEmptyChain);
  const { fitView, setCenter } = useReactFlow();
  const fittedSignature = useRef('');
  const chromeRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const skipEcho = useRef<Set<string>>(new Set());
  const writeTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    const onGesture = (event: Event) => event.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGesture);
    el.addEventListener('gesturechange', onGesture);
    el.addEventListener('gestureend', onGesture);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGesture);
      el.removeEventListener('gesturechange', onGesture);
      el.removeEventListener('gestureend', onGesture);
    };
  }, []);

  useEffect(() => {
    if (isEmptyChain) setPlannerOpen(true);
  }, [isEmptyChain]);

  useEffect(() => {
    if (!plannerOpen || isEmptyChain) return;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && chromeRef.current && !chromeRef.current.contains(target)) {
        setPlannerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isEmptyChain, plannerOpen]);

  const persistLocation = useCallback(
    (location: PeopleTravelLocation) => {
      if (readOnly) return;
      const key = `loc:${location.id}`;
      skipEcho.current.add(key);
      clearTimeout(writeTimers.current[key]);
      writeTimers.current[key] = setTimeout(() => {
        void upsertTripLocation(trip.id, location).finally(() => {
          skipEcho.current.delete(key);
        });
      }, WRITE_DEBOUNCE_MS);
    },
    [readOnly, trip.id],
  );

  const persistEvent = useCallback(
    (event: PeopleTravelEvent) => {
      if (readOnly) return;
      const key = `ev:${event.id}`;
      skipEcho.current.add(key);
      clearTimeout(writeTimers.current[key]);
      writeTimers.current[key] = setTimeout(() => {
        void upsertTripEvent(trip.id, event).finally(() => {
          skipEcho.current.delete(key);
        });
      }, WRITE_DEBOUNCE_MS);
    },
    [readOnly, trip.id],
  );

  const persistLeg = useCallback(
    (leg: PeopleTravelLeg) => {
      if (readOnly) return;
      void upsertTripLeg(trip.id, leg);
    },
    [readOnly, trip.id],
  );

  const buildNodes = useCallback((): Node[] => {
    const byLocation = new Map<string, PeopleTravelEvent[]>();
    for (const event of events) {
      const list = byLocation.get(event.locationId) || [];
      list.push(event);
      byLocation.set(event.locationId, list);
    }
    const nodes: Node[] = [];
    for (const location of locations) {
      const kids = (byLocation.get(location.id) || []).slice().sort((a, b) => a.y - b.y || a.x - b.x);
      const size = clusterSize(kids.length);
      nodes.push({
        id: location.id,
        type: 'locationCluster',
        position: { x: location.x, y: location.y },
        data: {
          location,
          eventCount: kids.length,
          readOnly,
          onLabelChange: (locationId: string, label: string) => {
            const current = locations.find(l => l.id === locationId);
            if (current) persistLocation({ ...current, label });
          },
        },
        style: { width: size.width, height: size.height },
        width: size.width,
        height: size.height,
      });
      kids.forEach((event, index) => {
        const fallback = defaultEventPosition(index);
        nodes.push({
          id: event.id,
          type: 'travelEvent',
          parentNode: location.id,
          extent: 'parent',
          position: { x: event.x ?? fallback.x, y: event.y ?? fallback.y },
          data: {
            event,
            readOnly,
            onTitleChange: (eventId: string, title: string) => {
              const current = events.find(e => e.id === eventId);
              if (current) persistEvent({ ...current, title });
            },
            onKindChange: (eventId: string, kind: PeopleTravelEventKind) => {
              const current = events.find(e => e.id === eventId);
              if (current) persistEvent({ ...current, kind });
            },
          },
          style: { width: CLUSTER_WIDTH - 32, height: 78 },
          width: CLUSTER_WIDTH - 32,
          height: 78,
        });
      });
    }
    return nodes;
  }, [events, locations, persistEvent, persistLocation, readOnly]);

  const buildEdges = useCallback((): Edge[] => {
    return legs
      .filter(leg => locations.some(l => l.id === leg.fromLocationId) && locations.some(l => l.id === leg.toLocationId))
      .map(leg => ({
        id: leg.id,
        type: 'travelLeg',
        source: leg.fromLocationId,
        target: leg.toLocationId,
        data: {
          leg,
          fromLabel: locations.find(l => l.id === leg.fromLocationId)?.label || '',
          toLabel: locations.find(l => l.id === leg.toLocationId)?.label || '',
          options: optionsForLeg(options, leg),
          readOnly,
          onModeChange: (legId: string, mode: PeopleTravelLegMode) => {
            const current = legs.find(l => l.id === legId);
            if (current) persistLeg({ ...current, mode });
          },
        },
      }));
  }, [legs, locations, options, persistLeg, readOnly]);

  const [nodes, setNodes, onNodesChange] = useNodesState(buildNodes());
  const [edges, setEdges, onEdgesChange] = useEdgesState(buildEdges());
  const signature = graphSignature(locations, events, legs, options);

  useEffect(() => {
    setNodes(prev => {
      const selectedIds = new Set(prev.filter(n => n.selected).map(n => n.id));
      return buildNodes().map(n => ({ ...n, selected: selectedIds.has(n.id) }));
    });
    setEdges(prev => {
      const selectedIds = new Set(prev.filter(e => e.selected).map(e => e.id));
      return buildEdges().map(e => ({ ...e, selected: selectedIds.has(e.id) }));
    });
  }, [buildEdges, buildNodes, setEdges, setNodes, signature]);

  const locationIdsKey = locations.map(l => l.id).sort().join(',');
  useEffect(() => {
    if (!locations.length) {
      fittedSignature.current = '';
      return;
    }
    if (fittedSignature.current === locationIdsKey) return;
    fittedSignature.current = locationIdsKey;
    const timer = window.setTimeout(() => {
      fitView({ padding: 0.24, duration: 280 });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [fitView, locationIdsKey, locations.length]);

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const edge = params.edges[0];
    const node = params.nodes[0];
    if (edge) {
      setSelection({ legId: edge.id });
      return;
    }
    if (node?.type === 'travelEvent') {
      const event = events.find(e => e.id === node.id);
      setSelection({ eventId: node.id, locationId: event?.locationId });
      return;
    }
    if (node?.type === 'locationCluster') {
      setSelection({ locationId: node.id });
      return;
    }
    setSelection(null);
  }, [events]);

  const handleNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (readOnly) return;
      if (node.type === 'locationCluster') {
        const current = locations.find(l => l.id === node.id);
        if (!current) return;
        const x = Math.round(node.position.x);
        const y = Math.round(node.position.y);
        if (x === current.x && y === current.y) return;
        persistLocation({ ...current, x, y });
        return;
      }
      if (node.type === 'travelEvent') {
        const current = events.find(e => e.id === node.id);
        if (!current) return;
        const x = Math.round(node.position.x);
        const y = Math.round(node.position.y);
        if (x === current.x && y === current.y) return;
        persistEvent({ ...current, x, y });
      }
    },
    [events, locations, persistEvent, persistLocation, readOnly],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (readOnly || !connection.source || !connection.target) return;
      if (connection.source === connection.target) return;
      if (locations.every(l => l.id !== connection.source) || locations.every(l => l.id !== connection.target)) {
        return;
      }
      const exists = legs.some(
        l => l.fromLocationId === connection.source && l.toLocationId === connection.target,
      );
      if (exists) return;
      void upsertTripLeg(trip.id, {
        fromLocationId: connection.source,
        toLocationId: connection.target,
        mode: 'drive',
      });
    },
    [legs, locations, readOnly, trip.id],
  );

  const handleAddLocation = useCallback(async () => {
    if (readOnly) return;
    const slot = nextLocationSlot(locations);
    await upsertTripLocation(trip.id, {
      label: 'New location',
      sortOrder: slot.sortOrder,
      x: slot.x,
      y: slot.y,
    });
    setCenter(slot.x + 140, slot.y + 80, { zoom: 0.85, duration: 280 });
  }, [locations, readOnly, setCenter, trip.id]);

  const handleAddEvent = useCallback(async () => {
    if (readOnly) return;
    const locationId = selection?.locationId;
    if (!locationId) return;
    const siblings = events.filter(e => e.locationId === locationId);
    const pos = defaultEventPosition(siblings.length);
    await upsertTripEvent(trip.id, {
      locationId,
      title: 'New event',
      kind: 'other',
      x: pos.x,
      y: pos.y,
    });
  }, [events, readOnly, selection?.locationId, trip.id]);

  const handleAddLeg = useCallback(async () => {
    if (readOnly || locations.length < 2) return;
    const from = locations[locations.length - 2];
    const to = locations[locations.length - 1];
    const exists = legs.some(l => l.fromLocationId === from.id && l.toLocationId === to.id);
    if (exists) return;
    await upsertTripLeg(trip.id, {
      fromLocationId: from.id,
      toLocationId: to.id,
      mode: 'drive',
    });
  }, [legs, locations, readOnly, trip.id]);

  const handleNodesDelete = useCallback(
    (deleted: Node[]) => {
      if (readOnly) return;
      for (const node of deleted) {
        if (node.type === 'locationCluster') void deleteTripLocation(trip.id, node.id);
        if (node.type === 'travelEvent') void deleteTripEvent(trip.id, node.id);
      }
    },
    [readOnly, trip.id],
  );

  const handleEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (readOnly) return;
      for (const edge of deleted) void deleteTripLeg(trip.id, edge.id);
    },
    [readOnly, trip.id],
  );

  return (
    <div className="people-travel-planner" ref={wrapperRef}>
      {locations.length === 0 ? (
        <div className="people-travel-planner-empty">
          <p>
            Tell the planner the overall chain — home, destinations, how you travel, and what happens at
            each stop. Clusters and legs will appear here.
          </p>
        </div>
      ) : null}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onSelectionChange={onSelectionChange}
        onNodeDragStop={handleNodeDragStop}
        onConnect={handleConnect}
        onNodesDelete={handleNodesDelete}
        onEdgesDelete={handleEdgesDelete}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
        panOnDrag={[1, 2]}
        panOnScroll
        preventScrolling
        zoomOnDoubleClick={false}
        minZoom={0.1}
        maxZoom={4}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#d8dee9" />
        <Controls showInteractive={false} />
        <MiniMap maskColor="rgba(244,246,250,0.7)" pannable zoomable />
      </ReactFlow>
      <div className="people-travel-planner-chrome nowheel" ref={chromeRef}>
        <div className={plannerOpen ? undefined : 'people-travel-planner-popout-slot-hidden'}>
          <PeopleTravelPlannerDock
            trip={trip}
            ownerUserId={ownerUserId}
            readOnly={readOnly}
            selection={selection}
            locations={locations}
            events={events}
            legs={legs}
            attachSignal={attachSignal}
            rebuildSignal={rebuildSignal}
          />
        </div>
        <PeopleTravelPlannerToolbar
          readOnly={readOnly}
          selection={selection}
          locations={locations}
          events={events}
          legs={legs}
          searchBusy={searchBusy}
          plannerOpen={plannerOpen}
          onAddLocation={() => void handleAddLocation()}
          onAddEvent={() => void handleAddEvent()}
          onAddLeg={() => void handleAddLeg()}
          onAttachScreenshot={() => {
            setPlannerOpen(true);
            setAttachSignal(n => n + 1);
          }}
          onSearchSelected={() => onSearchSelected(selection)}
          onRebuildChain={() => {
            setSelection(null);
            setPlannerOpen(true);
            setRebuildSignal(n => n + 1);
          }}
          onTogglePlanner={() => setPlannerOpen(open => !open)}
        />
      </div>
    </div>
  );
};

const PeopleTravelPlannerBoard: React.FC<Props> = props => (
  <ReactFlowProvider>
    <PeopleTravelPlannerBoardInner {...props} />
  </ReactFlowProvider>
);

export default PeopleTravelPlannerBoard;
